import { useNavigate, useLocation, NavLink } from "react-router-dom";
import { LayoutDashboard, Users2, BarChart3, UserCog, LogOut, Folder, LucideIcon, ChevronLeft, ChevronRight, AppWindow, Copy, MessageSquare, Instagram, Heart, HelpCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import logo from "@/assets/logo_header.png";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

function Item({ to, icon: Icon, children, exact = false }: { to: string; icon: LucideIcon; children: string; exact?: boolean }) {
  const { pathname } = useLocation();

  const isActive = exact
    ? pathname === to
    : pathname === to || pathname.startsWith(to + "/");

  const getNavCls = ({ isActive: navIsActive }: { isActive: boolean }) =>
    navIsActive ? "bg-primary/10 text-primary font-medium border-l-2 border-primary" : "hover:bg-muted/50";

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={children} isActive={isActive}>
        <NavLink to={to} end={exact} className={getNavCls}>
          <Icon className="h-4 w-4" />
          <span>{children}</span>
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function AppSidebar() {
  const nav = useNavigate();
  const { state, toggleSidebar } = useSidebar();
  const [loggingOut, setLoggingOut] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const isCollapsed = state === "collapsed";
  const paddingClass = isCollapsed ? "p-[22px]" : "p-[16px]";

  const shareUrl = "https://dev.elinia.ai";
  
  const shareMessage = `Oi!\n\nQueria compartilhar contigo a *elinia* — uma plataforma pensada pra facilitar a vida de quem cuida, deixando a rotina com pacientes mais organizada e leve.\n\nAntes de ver a ferramenta, esse é o conceito por trás: https://dev.elinia.ai/storytelling\n\nE aqui você pode testar a plataforma:  https://dev.elinia.ai\n\nAguardo teu feedback assim quem possível!`;

  async function logout() {
    try {
      setLoggingOut(true);
      await supabase.auth.signOut();
      nav("/auth");
    } finally {
      setLoggingOut(false);
    }
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      toast.success("Link copiado para a área de transferência!");
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      // Fallback para browsers antigos
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setLinkCopied(true);
        toast.success("Link copiado para a área de transferência!");
        setTimeout(() => setLinkCopied(false), 2000);
      } catch (err) {
        toast.error("Erro ao copiar link");
      }
      document.body.removeChild(textArea);
    }
  };

  const handleWhatsAppShare = () => {
    const message = encodeURIComponent(shareMessage);
    const whatsappUrl = `https://wa.me/?text=${message}`;
    window.open(whatsappUrl, "_blank");
    toast.success("WhatsApp aberto!");
  };

  const handleInstagramShare = async () => {
    try {
      // Copiar link primeiro
      await navigator.clipboard.writeText(shareUrl);
      toast.success(
        "Link copiado! Cole no Instagram Stories ou em uma publicação.",
        {
          description: "Abra o Instagram e cole o link que foi copiado.",
          duration: 5000,
        }
      );
      
      // Tentar abrir Instagram (pode não funcionar em todos os browsers)
      // Instagram não permite compartilhamento direto via web, então apenas copiamos o link
    } catch (error) {
      toast.error("Erro ao copiar link para Instagram");
    }
  };

  return (
    <Sidebar collapsible="icon" className="border-r bg-white">
      <SidebarRail />
      <div className={`border-b h-[73px] flex items-center gap-3 group-data-[collapsible=icon]:justify-center ${paddingClass} relative`}>
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-2 h-7 w-7 hover:bg-muted group-data-[collapsible=icon]:relative group-data-[collapsible=icon]:right-auto group-data-[collapsible=icon]:left-auto group-data-[collapsible=icon]:mx-auto"
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
        >
          {state === "collapsed" ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
        <img src={logo} alt="Elinia" className="h-auto w-auto group-data-[collapsible=icon]:hidden" />
      </div>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <Item to="/app" icon={LayoutDashboard} exact={true}>Início</Item>
              <Item to="/app/pacientes" icon={Users2}>Pacientes</Item>
              <Item to="/app/categorias" icon={Folder}>Categorias</Item>
              <Item to="/app/aplicativos" icon={AppWindow}>Aplicativos</Item>
              <Item to="/app/estatisticas" icon={BarChart3}>Estatísticas</Item>
              <SidebarSeparator className="my-1" />
              <Item to="/app/minha-conta" icon={UserCog}>Minha conta</Item>
              <Item to="/app/suporte" icon={HelpCircle}>Suporte</Item>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        {/* Seção de Compartilhamento - Logo acima da linha */}
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  tooltip={isCollapsed ? "Compartilhe com seus colegas" : undefined}
                  className="hover:bg-primary/10 hover:text-primary w-full h-auto min-h-12 py-2"
                >
                  <Heart className="h-4 w-4" />
                  {!isCollapsed && (
                    <div className="flex flex-col items-start flex-1 text-left">
                      <span className="text-xs text-muted-foreground leading-tight">
                        Estamos facilitando sua rotina?
                      </span>
                      <span className="text-xs font-medium leading-tight">
                        Compartilhe com seus colegas
                      </span>
                    </div>
                  )}
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side={isCollapsed ? "right" : "top"} className="w-56">
                <DropdownMenuItem onClick={handleCopyLink} className="cursor-pointer">
                  <Copy className="h-4 w-4 mr-2" />
                  <span>Copiar link</span>
                  {linkCopied && (
                    <span className="ml-auto text-xs text-green-600">✓ Copiado!</span>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleWhatsAppShare} className="cursor-pointer">
                  <MessageSquare className="h-4 w-4 mr-2 text-green-600" />
                  <span>Compartilhar no WhatsApp</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleInstagramShare} className="cursor-pointer">
                  <Instagram className="h-4 w-4 mr-2 text-pink-600" />
                  <span>Compartilhar no Instagram</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>

        {/* Linha divisória */}
        <div className="border-t" />

        {/* Botão Sair - Abaixo da linha */}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={logout}
              disabled={loggingOut}
              tooltip="Sair da conta"
              className="hover:bg-destructive/10 hover:text-destructive"
            >
              <LogOut className="h-4 w-4" />
              <span>{loggingOut ? "Saindo..." : "Sair"}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
